import "./lib/toast-interceptor";
import { startVersionCheck } from "./lib/version-check";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import "./index.css";
import { initSentry } from "./lib/sentry";
import { initPreferences } from "./stores/useUserPreferences";
import { ConfirmProvider } from "./providers/ConfirmProvider";
import { installDevToolsShield } from "./lib/devtools-shield";
import { installDiagnosticsCollector, recordBreadcrumb } from "./lib/diagnostics";
import { recordJournalEvent } from "./lib/event-journal";
import { z } from "zod";
import { viZodErrorMap } from "@apv-erp/status";

// Việt hóa toàn bộ thông báo lỗi Zod mặc định (tránh lộ tiếng Anh trên giao diện)
z.setErrorMap(viZodErrorMap as any);

installDevToolsShield();
installDiagnosticsCollector();

initSentry();

initPreferences();

// Tự động làm mới trang khi có lỗi tải chunk động sau khi hệ thống vừa deploy phiên bản mới
if (typeof window !== "undefined") {
    window.addEventListener("vite:preloadError", (event) => {
        console.warn("[Vite] Chunk load error detected (version mismatch after deploy). Reloading page...", event);
        window.location.reload();
    });
}

// Tab mở lâu: báo khi đã deploy phiên bản mới để người dùng tải lại (trình duyệt giữ bản JS cũ tới khi tải lại)
startVersionCheck();

// Import generated route tree
import { router } from "./routeTree";
import { useNavigationHistoryStore } from "./stores/useNavigationHistory";

// ── Track Navigation History for Smart Back Return ──
if (router.state?.location) {
    recordJournalEvent({ kind: "route", name: router.state.location.pathname });
    useNavigationHistoryStore.getState().pushRoute({
        pathname: router.state.location.pathname,
        search: router.state.location.searchStr || '',
        href: router.state.location.href,
    });
}

router.subscribe('onResolved', ({ toLocation }) => {
    if (toLocation) {
        useNavigationHistoryStore.getState().pushRoute({
            pathname: toLocation.pathname,
            search: toLocation.searchStr || '',
            href: toLocation.href,
        });
        recordBreadcrumb('navigate', `Chuyển trang: ${toLocation.pathname}`, toLocation.pathname);
        // Nhật ký v2: chỉ ghi đường dẫn, không ghi query (?q có thể chứa dữ liệu người dùng gõ)
        recordJournalEvent({ kind: "route", name: toLocation.pathname });
    }
});

import { AuthGuard } from "./components/auth/AuthGuard";
import { ReactQueryProvider } from "./lib/react-query";
import { TrpcProvider } from "./lib/trpc-provider";
import { ConfigPrefetcher } from "./components/shared/ConfigPrefetcher";

// Register things for typesafety
declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    const isLandingPage = window.location.pathname === '/landingpage' || window.location.pathname.startsWith('/landingpage/');
    
    root.render(
        <StrictMode>
            <TrpcProvider>
                <ConfirmProvider>
                    <AuthGuard>
                        {!isLandingPage && <ConfigPrefetcher />}
                        <RouterProvider router={router} />
                    </AuthGuard>
                </ConfirmProvider>
            </TrpcProvider>
        </StrictMode>
    );
}
